import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { readApiETag } from '@/features/pipeline/types';
import type { ActivityLinkType, PagedResult, TaskListItem, TaskResponse, TaskState } from './types';

export const TASKS_QUERY_KEY = ['tasks'] as const;

export interface TaskFilters {
  state?: TaskState | null;
  assigneeUserId?: string | null;
  linkType?: ActivityLinkType | null;
  linkId?: string | null;
}

function qs(params: Record<string, string | null | undefined>): string {
  const sp = new URLSearchParams({ take: '200' });
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  return `?${sp.toString()}`;
}

/** Lista tarefas (filtros opcionais). Cacheada por filtros. `enabled` gateia o fetch (default true). */
export function useTasks(filters: TaskFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...TASKS_QUERY_KEY, filters],
    enabled,
    queryFn: () =>
      apiFetch<PagedResult<TaskListItem>>(
        `/api/tasks${qs({
          state: filters.state,
          assigneeUserId: filters.assigneeUserId,
          linkType: filters.linkType,
          linkId: filters.linkId,
        })}`,
      ),
  });
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  assigneeUserId: string;
  dueDateUtc?: string | null;
  linkType: ActivityLinkType;
  linkId: string;
}

export function useCreateTask() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => apiFetch<TaskResponse>('/api/tasks', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      toast('Tarefa criada.', 'default');
    },
    onError: () => toast('Não foi possível criar a tarefa.', 'danger'),
  });
}

export interface UpdateTaskInput {
  id: string;
  title: string;
  description?: string | null;
  assigneeUserId: string;
  dueDateUtc?: string | null;
}

export function useUpdateTask() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (input: UpdateTaskInput) => {
      // ListItem não traz ETag — busca o atual p/ o If-Match. readApiETag (achado F8) tolera a
      // variação de casing do acrônimo, mesma cautela do resto do domínio de Deal.
      const current = await apiFetch<TaskResponse>(`/api/tasks/${input.id}`);
      return apiFetch<TaskResponse>(`/api/tasks/${input.id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${readApiETag(current)}"` },
        body: {
          title: input.title,
          description: input.description ?? null,
          assigneeUserId: input.assigneeUserId,
          dueDateUtc: input.dueDateUtc ?? null,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      toast('Tarefa atualizada.', 'default');
    },
    onError: () => toast('Não foi possível atualizar a tarefa.', 'danger'),
  });
}

/** Muda o estado (incl. reabertura). Busca o ETag atual p/ o If-Match. */
export function useChangeTaskState() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ id, state }: { id: string; state: TaskState }) => {
      const current = await apiFetch<TaskResponse>(`/api/tasks/${id}`);
      return apiFetch<TaskResponse>(`/api/tasks/${id}/state`, {
        method: 'POST',
        headers: { 'If-Match': `"${readApiETag(current)}"` },
        body: { state },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
    onError: (err) => {
      if ((err as ApiError).status === 412) toast('A tarefa mudou; atualizei a lista.', 'warn');
      else toast('Não foi possível mudar o estado.', 'danger');
      void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}
